// Base
import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";

// Store
import { useTemplateStore } from "@/zustand/templateStore";
import { useProductData } from "@/zustand/productStore";
import { useCartStore } from "@/zustand/cartStore";
import { useShapePaypalData } from "@/zustand/shapePaypalStore";

// Utils
import captureException from "@/func/sentryException";
import RiskService from "@/utils/risk/RiskProvider";

// hooks
import { useRiskJsLoader } from "@/hooks/useRiskJs";

// Components
import Form from "@/components/Forms/Form";

// Types
import type { AxiosRequestConfig } from "axios";
import type { ProductDataExtended } from "@/types/types";
import type {
  PaypalData,
  CreditCardData,
  ApplePayData,
  GooglePayData,
} from "@/types/types";

interface GooglePayResponseSuccess {
  result: "SUCCESS";
  message: { orderId: string };
}

interface GooglePayResponseError {
  result: "ERROR";
  message: string;
}

interface GooglePayResponseRedirect {
  result: "MERC_REDIRECT";
  message: { url?: string; script?: string };
}

type GooglePayResponse =
  | GooglePayResponseSuccess
  | GooglePayResponseError
  | GooglePayResponseRedirect;

const FormWrapper = () => {
  const { isLoading, setLoadingState, form } = useTemplateStore();
  const {
    paypalHandler,
    warrantyState,
    selectedProduct,
    discountCode,
    isDiscountApplied,
  } = useCartStore();

  const productData = useProductData() as ProductDataExtended;

  const riskReady = useRiskJsLoader(
    productData?.paymentsConfig?.checkoutcomPublisherKey || "",
  );

  const riskService = useMemo(() => {
    if (!riskReady) return null;
    return new RiskService(
      productData?.paymentsConfig?.checkoutcomPublisherKey || "",
    );
  }, [riskReady, productData?.paymentsConfig?.checkoutcomPublisherKey]);
  const [backError, setBackError] = useState<string | undefined>();
  const backErrorField = useRef<HTMLDivElement>(null);
  const isSubmitting = useRef(false);
  const checkoutToken = useRef(window.checkoutToken || crypto.randomUUID());

  const scrollIntoView = (element: HTMLElement | null) => {
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  };

  useEffect(() => {
    if (backError) {
      scrollIntoView(backErrorField?.current);
    }
  }, [backError]);

  const {
    available: shapePaypalAvailable,
    account: shapePaypalAccount,
    resolve: resolveShapePaypal,
    rule_id: shapePaypalRuleId,
  } = useShapePaypalData();
  const shapeResolveTriggered = useRef(false);

  useEffect(() => {
    const campaignId = productData?.konnektive_campaign_id?.trim();

    if (!campaignId || shapeResolveTriggered.current) return;
    shapeResolveTriggered.current = true;

    resolveShapePaypal({
      campaignId,
      countryCode: productData?.country_code,
      affiliateTag: window.affiliate,
      urlParameter: new URLSearchParams(window.location.search).get(
        "url_parameter",
      ),
      postId: window.postId,
    });
  }, [
    productData?.konnektive_campaign_id,
    productData?.country_code,
    resolveShapePaypal,
  ]);

  const handleMercRedirect = (message: { url?: string; script?: string }) => {
    if (message?.url) {
      window.location.href = message.url;
    } else if (message?.script) {
      eval(message.script);
    }
  };

  // --- PayPal ---
  const handlePaypalSubmit = async (): Promise<void> => {
    const placedOrderId = sessionStorage.getItem(
      "cc_order_placed_" + checkoutToken.current,
    );
    if (placedOrderId) {
      window.location.href =
        productData?.next_page_on_success + "?orderId=" + placedOrderId;
      return;
    }
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    const warrantyType = productData?.select_warranty_type_cc;
    const warrantyTitle =
      warrantyType?.label?.toLowerCase() === "extended warranty"
        ? form?.tr_extended_warranty_label
        : form?.tr_shipping_protection_label;

    const paypalData: PaypalData = {
      checkoutToken: checkoutToken.current,
      products: {
        currencyCode: productData?.currency_code,
        paypalMerchant: productData?.paypal_merchant,
        warrantyTitle: warrantyTitle,
        campaignId: productData?.konnektive_campaign_id?.trim(),
        product: selectedProduct,
        dynamic_descriptor: productData?.dynamic_descriptor ?? null,
        nextStep: productData?.next_page_on_success,
        multiplyPriceByAmount: productData?.multiply_price_by_amount,
        warranty_price:
          productData?.add_warranty_for_product === false
            ? 0
            : selectedProduct?.warranty_price,
        warrantyPriceCalculation:
          productData?.add_warranty_for_product === false,
        isWarrantySelected: productData?.add_warranty_for_product ?? false,
        warrantyState: warrantyState,
        warrantyType: productData?.select_warranty_type_cc ?? null,
        postId: window.postId,
        variants: null,
      },
      nextStep: productData?.next_page_on_success,
      upsell: productData?.paypal_upsell
        ? productData?.paypal_upsell_settings
        : null,
      thankYou: productData?.paypal_thank_you_page,
      return_to: window.location.href,
      affiliate: window.affiliate,
      // Konnektive merchant/biller id Shape resolved for this checkout - only
      // relevant (and only sent) when routing through the Shape/bank-store
      // proxy, so the order actually processes through that exact account.
      merchantId: shapePaypalAvailable
        ? shapePaypalAccount?.merchant_id
        : undefined,
      ruleId: shapePaypalAvailable ? shapePaypalRuleId : undefined,
      // Bank-store proxy host for this resolved account - there is no static
      // fallback, so the paypal-shape endpoint can't build an order without it.
      bankStoreUrl: shapePaypalAvailable
        ? shapePaypalAccount?.checkout_url
        : undefined,
      data: {
        email:
          (
            document.querySelector(
              'input[name="email"]',
            ) as HTMLInputElement | null
          )?.value?.trim() || "",
      },
      discountsEnabled: productData?.enable_discount_codes,
      appliedDiscountCode: discountCode,
      isDiscountApplied: isDiscountApplied,
      dynamic_descriptor: productData?.dynamic_descriptor ?? null,
      send_dynamic_descriptor: productData?.send_dynamic_descriptor ?? false,
    };

    try {
      setLoadingState(true);
      // When Shape has a routed PayPal account available for this checkout,
      // process the order through the bank-store proxy instead of the
      // static/local PayPal flow. If Shape had nothing available (or this
      // site isn't opted in), this falls through to the existing endpoint
      // unchanged.
      const paypalEndpoint = shapePaypalAvailable
        ? "/wp-json/checkout/v1/order/paypal-shape"
        : "/wp-json/checkout/v1/order/paypal";
      const response = await axios.post<
        string | { result: "ERROR"; message: string }
      >(paypalEndpoint, paypalData);
      const data = response.data;
      const errorMessage =
        typeof data === "object" && data !== null && data.result === "ERROR"
          ? data.message
          : data === "ERROR"
            ? "Something went wrong. Please try again."
            : null;
      if (errorMessage !== null) {
        setBackError(errorMessage);
        setLoadingState(false);
        isSubmitting.current = false;
        captureException(new Error(`Checkout payment error: ${errorMessage}`), {
          tags: {
            type: "Javascript error",
            error: "checkout_payment_submission_failed",
          },
          extra: { response_error: errorMessage, payment_type: "paypal_order" },
          contexts: { request_data: paypalData },
        });
      } else {
        window.location.href = data as string;
      }
    } catch (error: any) {
      setLoadingState(false);
      isSubmitting.current = false;
      setBackError(error?.message);
      captureException(error, {
        tags: {
          type: "Javascript error",
          error: "checkout_payment_submission_failed",
        },
        extra: {
          response_error: error?.response?.data,
          payment_type: "paypal_order",
        },
        contexts: { request_data: paypalData },
      });
    }
  };

  // --- Credit Card ---
  const handleCreditCardSubmit = async (
    formData: CreditCardData,
  ): Promise<void> => {
    const placedOrderId = sessionStorage.getItem(
      "cc_order_placed_" + checkoutToken.current,
    );
    if (placedOrderId) {
      window.location.href =
        productData?.next_page_on_success + "?orderId=" + placedOrderId;
      return;
    }
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    const navigator = window.navigator;
    setLoadingState(true);
    const riskSessionId = riskService
      ? ((await riskService.getSessionId()) ?? null)
      : null;

    const warrantyType = productData?.select_warranty_type_cc;
    const warrantyTitle =
      warrantyType?.label?.toLowerCase() === "extended warranty"
        ? form?.tr_extended_warranty_label
        : form?.tr_shipping_protection_label;

    const browserData = {
      acceptHeader: "application/json",
      colorDepth: screen.colorDepth,
      javaEnabled: navigator.javaEnabled(),
      javaScriptEnabled: true,
      language: navigator.language,
      screenHeight: screen.height,
      screenWidth: screen.width,
      timeZoneOffset: new Date().getTimezoneOffset().toString(),
      userAgent: navigator.userAgent,
    };

    const requestData = {
      data: formData,
      checkoutToken: checkoutToken.current,
      affiliate: window.affiliate,
      products: {
        currencyCode: productData?.currency_code,
        campaignId: productData?.konnektive_campaign_id?.trim(),
        isWarrantySelected: productData?.add_warranty_for_product,
        multiplyPriceByAmount: productData?.multiply_price_by_amount,
        nextStep: productData?.next_page_on_success,
        product: selectedProduct,
        warranty_price:
          productData?.add_warranty_for_product === false
            ? 0
            : selectedProduct?.warranty_price,
        dynamic_descriptor: productData?.dynamic_descriptor ?? null,
        send_dynamic_descriptor: productData?.send_dynamic_descriptor ?? false,
        warrantyType: productData?.select_warranty_type_cc ?? null,
        warrantyTitle: warrantyTitle,
        warrantyPriceCalculation:
          productData?.add_warranty_for_product === false,
        postId: window.postId,
        warrantyState: warrantyState,
        variants: null,
      },
      upsell_path: productData?.next_page_on_success,
      return_to: window.location.href,
      browserData,
      paypalMerchant: productData?.paypal_merchant,
      discountsEnabled: productData?.enable_discount_codes,
      appliedDiscountCode: discountCode,
      bundles: productData?.show_bundle_design,
      isDiscountApplied: isDiscountApplied,
      riskSessionId: riskSessionId,
    };

    try {
      const response = await axios.post(
        "/wp-json/checkout/v1/order",
        requestData,
      );
      const data = response.data;
      if (data.result === "ERROR") {
        setBackError(data.message);
        setLoadingState(false);
        isSubmitting.current = false;
        captureException(new Error(`Checkout payment error: ${data.message}`), {
          tags: {
            type: "Javascript error",
            error: "checkout_payment_submission_failed",
          },
          extra: { response_error: data.message, payment_type: "credit_card" },
          contexts: { request_data: requestData },
        });
      } else if (data.result === "SUCCESS") {
        sessionStorage.setItem(
          "cc_order_placed_" + checkoutToken.current,
          data.message.orderId,
        );
        window.location.href =
          productData?.next_page_on_success +
          "?orderId=" +
          data.message.orderId;
      } else if (data.result === "MERC_REDIRECT") {
        handleMercRedirect(data.message);
      }
    } catch (error: any) {
      setLoadingState(false);
      isSubmitting.current = false;
      setBackError(error?.message);
      captureException(error, {
        tags: {
          type: "Javascript error",
          error: "checkout_payment_submission_failed",
        },
        extra: { response_error: error?.message, payment_type: "credit_card" },
        contexts: { request_data: requestData },
      });
    }
  };

  // --- Apple Pay ---
  const handleApplePaySubmit = async (
    applePayData: ApplePayData,
    options?: AxiosRequestConfig,
  ): Promise<void> => {
    const placedOrderId = sessionStorage.getItem(
      "cc_order_placed_" + checkoutToken.current,
    );
    if (placedOrderId) {
      window.location.href =
        productData?.next_page_on_success + "?orderId=" + placedOrderId;
      return;
    }
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    const navigator = window.navigator;
    const warrantyType = productData?.select_warranty_type_cc;
    const warrantyTitle =
      warrantyType?.label?.toLowerCase() === "extended warranty"
        ? form?.tr_extended_warranty_label
        : form?.tr_shipping_protection_label;

    const browserData = {
      acceptHeader: "application/json",
      colorDepth: screen.colorDepth,
      javaEnabled: navigator.javaEnabled(),
      javaScriptEnabled: true,
      language: navigator.language,
      screenHeight: screen.height,
      screenWidth: screen.width,
      timeZoneOffset: new Date().getTimezoneOffset().toString(),
      userAgent: navigator.userAgent,
    };

    const requestData = {
      apple_pay_token: applePayData.apple_pay_token,
      customer: applePayData.customer,
      checkoutToken: checkoutToken.current,
      affiliate: window.affiliate,
      products: {
        currencyCode: productData?.currency_code,
        campaignId: productData?.konnektive_campaign_id?.trim(),
        isWarrantySelected: productData?.add_warranty_for_product,
        multiplyPriceByAmount: productData?.multiply_price_by_amount,
        nextStep: productData?.next_page_on_success,
        product: selectedProduct,
        warranty_price:
          productData?.add_warranty_for_product === false
            ? 0
            : selectedProduct?.warranty_price,
        dynamic_descriptor: productData?.dynamic_descriptor ?? null,
        send_dynamic_descriptor: productData?.send_dynamic_descriptor ?? false,
        warrantyType: productData?.select_warranty_type_cc ?? null,
        warrantyTitle: warrantyTitle,
        warrantyPriceCalculation:
          productData?.add_warranty_for_product == false,
        postId: window.postId,
        warrantyState: warrantyState,
        variants: null,
      },
      upsell_path: productData?.next_page_on_success,
      return_to: window.location.href,
      browserData,
      paypalMerchant: productData?.paypal_merchant,
      discountsEnabled: productData?.enable_discount_codes,
      appliedDiscountCode: discountCode,
      bundles: productData?.show_bundle_design,
      isDiscountApplied: isDiscountApplied,
    };

    try {
      setLoadingState(true);
      const response = await axios.post(
        "/wp-json/wallet/v1/apple/pay",
        requestData,
        options,
      );
      const data = response.data;
      if (data.result === "ERROR") {
        setBackError(data.message);
        setLoadingState(false);
        isSubmitting.current = false;
        captureException(new Error(`Checkout payment error: ${data.message}`), {
          tags: {
            type: "Javascript error",
            error: "checkout_payment_submission_failed",
          },
          extra: { response_error: data.message, payment_type: "apple_pay" },
          contexts: { request_data: requestData },
        });
      } else if (data.result === "SUCCESS") {
        sessionStorage.setItem(
          "cc_order_placed_" + checkoutToken.current,
          data.message.orderId,
        );
        window.location.href =
          productData?.next_page_on_success +
          "?orderId=" +
          data.message.orderId;
      } else if (data.result === "MERC_REDIRECT") {
        handleMercRedirect(data.message);
      }
    } catch (error: any) {
      setLoadingState(false);
      isSubmitting.current = false;
      setBackError(error?.message);
      captureException(error, {
        tags: {
          type: "Javascript error",
          error: "checkout_payment_submission_failed",
        },
        extra: { response_error: error?.message, payment_type: "apple_pay" },
        contexts: { request_data: requestData },
      });
    }
  };

  // --- Google Pay ---
  const handleGooglePaySubmit = async (
    googlePayData: GooglePayData,
    options: AxiosRequestConfig = {},
  ): Promise<void> => {
    const placedOrderId = sessionStorage.getItem(
      "cc_order_placed_" + checkoutToken.current,
    );
    if (placedOrderId) {
      window.location.href =
        (useProductData.getState() as ProductDataExtended)
          ?.next_page_on_success +
        "?orderId=" +
        placedOrderId;
      return;
    }
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    const productData = useProductData();
    const { warrantyState, selectedProduct, discountCode, isDiscountApplied } =
      useCartStore();

    const navigator = window.navigator;

    const warrantyType = productData?.select_warranty_type_cc;
    const warrantyTitle =
      warrantyType?.label?.toLowerCase() === "extended warranty"
        ? form?.tr_extended_warranty_label
        : form?.tr_shipping_protection_label;

    const browserData = {
      acceptHeader: "application/json",
      colorDepth: screen.colorDepth,
      javaEnabled: navigator.javaEnabled(),
      javaScriptEnabled: true,
      language: navigator.language,
      screenHeight: screen.height,
      screenWidth: screen.width,
      timeZoneOffset: new Date().getTimezoneOffset().toString(),
      userAgent: navigator.userAgent,
    };

    const requestData = {
      googlePayToken: googlePayData.google_pay_token,
      customer: googlePayData.customer,
      checkoutToken: checkoutToken.current,
      affiliate: window.affiliate,
      products: {
        currencyCode: productData?.currency_code,
        campaignId: productData?.konnektive_campaign_id?.trim(),
        isWarrantySelected: productData?.add_warranty_for_product,
        multiplyPriceByAmount: productData?.multiply_price_by_amount,
        nextStep: productData?.next_page_on_success,
        product: selectedProduct,
        warranty_price:
          productData?.add_warranty_for_product === false
            ? 0
            : selectedProduct?.warranty_price,
        dynamic_descriptor: productData?.dynamic_descriptor ?? null,
        send_dynamic_descriptor: productData?.send_dynamic_descriptor ?? false,
        warrantyType: productData?.select_warranty_type_cc ?? null,
        warrantyTitle,
        warrantyPriceCalculation:
          productData?.add_warranty_for_product === false,
        postId: window.postId,
        warrantyState,
        variants: null,
      },
      upsell_path: productData?.next_page_on_success,
      return_to: window.location.href,
      browserData,
      paypalMerchant: productData?.paypal_merchant,
      discountsEnabled: productData?.enable_discount_codes,
      appliedDiscountCode: discountCode,
      bundles: productData?.show_bundle_design,
      isDiscountApplied,
    };

    try {
      setLoadingState(true);

      const response = await axios.post<GooglePayResponse>(
        "/wp-json/wallet/v1/google/pay",
        requestData,
        options,
      );

      const data = response.data;

      switch (data.result) {
        case "SUCCESS":
          sessionStorage.setItem(
            "cc_order_placed_" + checkoutToken.current,
            data.message.orderId,
          );
          window.location.href =
            productData?.next_page_on_success +
            "?orderId=" +
            data.message.orderId;
          break;

        case "ERROR":
          setBackError(data.message);
          isSubmitting.current = false;
          const errorObj = new Error(`Checkout payment error: ${data.message}`);
          captureException(errorObj, {
            tags: {
              type: "Javascript error",
              error: "checkout_payment_submission_failed",
            },
            extra: { response_error: data.message, payment_type: "google_pay" },
            contexts: { request_data: requestData },
          });
          break;

        case "MERC_REDIRECT":
          handleMercRedirect(data.message);
          break;
      }
    } catch (error: unknown) {
      setLoadingState(false);
      isSubmitting.current = false;

      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error);

      setBackError(errorMessage);

      captureException(error, {
        tags: {
          type: "Javascript error",
          error: "checkout_payment_submission_failed",
        },
        extra: { response_error: errorMessage, payment_type: "google_pay" },
        contexts: { request_data: requestData },
      });
    } finally {
      setLoadingState(false);
    }
  };
  return (
    <>
      <Form
        formSubmited={isLoading}
        handleCreditCardSubmit={handleCreditCardSubmit}
        handlePaypalSubmit={handlePaypalSubmit}
        handleApplePaySubmit={handleApplePaySubmit}
        handleGooglePaySubmit={handleGooglePaySubmit}
        paypalHandler={paypalHandler}
      />
      {backError && (
        <div
          ref={backErrorField}
          className="bg-cc-dark-red-2 flex h-8 items-center justify-center rounded-sm text-white"
        >
          {backError}
        </div>
      )}
    </>
  );
};

export { FormWrapper };
